import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { animate, motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./OriginalPaywall.module.scss"

/**
 * OriginalPaywall — an original, self-contained, NON-privacy paywall.
 *
 * Divergence brief (see claude-paywalls/_ledger.md). The prior fifteen paywalls
 * clustered — early ones on a dark hero + glowing card, the recent six on a
 * light, photo-led swipe/grid/map with an input-driven gimmick. This one moves
 * every axis on purpose:
 *   - Value frame:   QUALITY / ORIGINALS — "kept exactly as you shot it, full
 *                    resolution, never compressed." No prior paywall pitched
 *                    image quality. It is the whole argument here.
 *   - Layout:        a full-bleed BEFORE/AFTER SPLIT with a draggable divider,
 *                    matted like a gallery print. No hero-card, no perk grid.
 *   - Interaction:   DRAG THE DIVIDER — a connoisseur's loupe you scrub across
 *                    the seam; tap the filmstrip to compare another original.
 *   - Register:      CONNOISSEUR / CRAFTED / reverent (quality pride), not
 *                    urgency, not playful, not cozy.
 *   - Art direction: GALLERY-LIGHT + PHOTOGRAPHIC + EDITORIAL EXIF TYPE — warm
 *                    paper wall, a single matted print, a technical metadata
 *                    readout. One brand-blue accent. No dark theme, no particles.
 *
 * The pitch: everyone else quietly downscales your photos to save space;
 * CloudVault keeps every original untouched, full resolution, backed up. The
 * yearly price is the prominent figure and drives the sale.
 */

interface Photo {
  id: string
  src: string
  title: string
  loc: string
  // "original" (kept) EXIF
  dim: string
  mp: string
  size: string
  fmt: string
  // "compressed by others" figures
  lossyDim: string
  lossyMp: string
  lossySize: string
}

const PHOTOS: Photo[] = [
  {
    id: "macaw",
    src: "/original/macaw.jpg",
    title: "Scarlet macaw",
    loc: "Costa Rica",
    dim: "6048 × 8064",
    mp: "48.7",
    size: "26.4 MB",
    fmt: "HEIC",
    lossyDim: "756 × 1008",
    lossyMp: "0.8",
    lossySize: "184 KB",
  },
  {
    id: "market",
    src: "/original/market.jpg",
    title: "Spice souk",
    loc: "Marrakech",
    dim: "5472 × 8208",
    mp: "44.9",
    size: "22.1 MB",
    fmt: "HEIC",
    lossyDim: "684 × 1026",
    lossyMp: "0.7",
    lossySize: "162 KB",
  },
  {
    id: "portrait",
    src: "/original/portrait.jpg",
    title: "Freckles, no.4",
    loc: "Studio roll",
    dim: "4000 × 6000",
    mp: "24.0",
    size: "14.8 MB",
    fmt: "RAW",
    lossyDim: "500 × 750",
    lossyMp: "0.4",
    lossySize: "121 KB",
  },
  {
    id: "forest",
    src: "/original/forest.jpg",
    title: "Beech slope",
    loc: "Kaçkar range",
    dim: "6336 × 9504",
    mp: "60.2",
    size: "31.7 MB",
    fmt: "HEIC",
    lossyDim: "792 × 1188",
    lossyMp: "0.9",
    lossySize: "203 KB",
  },
]

/**
 * Bake a genuinely over-compressed version of an image in-browser: downsample
 * hard, re-encode as low-quality JPEG (real DCT blocking), then hold it big.
 * Same-origin images, so the canvas is never tainted.
 */
function makeCompressed(src: string, factor = 11): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      try {
        const w = img.naturalWidth
        const h = img.naturalHeight
        const sw = Math.max(10, Math.round(w / factor))
        const sh = Math.max(10, Math.round(h / factor))
        const small = document.createElement("canvas")
        small.width = sw
        small.height = sh
        const sctx = small.getContext("2d")!
        sctx.imageSmoothingEnabled = true
        sctx.drawImage(img, 0, 0, sw, sh)

        const big = document.createElement("canvas")
        big.width = w
        big.height = h
        const bctx = big.getContext("2d")!
        bctx.imageSmoothingEnabled = false
        bctx.drawImage(small, 0, 0, sw, sh, 0, 0, w, h)
        resolve(big.toDataURL("image/jpeg", 0.32))
      } catch (e) {
        reject(e)
      }
    }
    img.onerror = reject
    img.src = src
  })
}

const OriginalPaywall = () => {
  const { state } = useAppState()
  const reduce = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const savePercent =
    yearlyProduct && monthlyProduct
      ? Math.round((1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100)
      : 79

  const [selected, setSelected] = useState(0)
  const [divider, setDivider] = useState(0.5) // 0..1, fraction that is COMPRESSED
  const [mounted, setMounted] = useState(false)
  const [compressed, setCompressed] = useState<Record<string, string>>({})

  const stageRef = useRef<HTMLDivElement>(null)
  const sweepRef = useRef<ReturnType<typeof animate> | null>(null)
  const draggingRef = useRef(false)
  const touchedRef = useRef(false)

  const active = PHOTOS[selected]
  const pct = divider * 100

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 60)
    return () => clearTimeout(t)
  }, [])

  // Build the compressed twin for whichever photo is on stage (cached).
  useEffect(() => {
    let cancelled = false
    if (compressed[active.id]) return
    makeCompressed(active.src)
      .then((url) => {
        if (!cancelled) setCompressed((m) => ({ ...m, [active.id]: url }))
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [active.id, active.src, compressed])

  // Auto-demo: once mounted, sweep the loupe across the seam so the effect
  // reveals itself, then rest just past centre. Any touch cancels it.
  useEffect(() => {
    if (!mounted || reduce || touchedRef.current) return
    const controls = animate(0.5, [0.5, 0.16, 0.84, 0.54], {
      duration: 3.1,
      times: [0, 0.32, 0.72, 1],
      ease: [0.4, 0, 0.2, 1],
      delay: 0.5,
      onUpdate: (v) => {
        if (!touchedRef.current) setDivider(v)
      },
    })
    sweepRef.current = controls
    return () => controls.stop()
  }, [mounted, reduce])

  const setFromClientX = useCallback((clientX: number) => {
    const el = stageRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const x = (clientX - r.left) / r.width
    setDivider(Math.min(0.94, Math.max(0.06, x)))
  }, [])

  const onPointerDown = (e: React.PointerEvent) => {
    touchedRef.current = true
    sweepRef.current?.stop()
    draggingRef.current = true
    ;(e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId)
    setFromClientX(e.clientX)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    setFromClientX(e.clientX)
  }
  const onPointerUp = () => {
    draggingRef.current = false
  }

  const pickPhoto = (i: number) => {
    touchedRef.current = true
    sweepRef.current?.stop()
    setSelected(i)
  }

  const handleClose = () => native.postNativeEvent("close")
  const buyYearly = () => {
    if (yearlyProduct) native.subscribe(yearlyProduct)
  }
  const buyMonthly = () => {
    if (monthlyProduct) native.subscribe(monthlyProduct)
  }

  const compressedSrc = compressed[active.id]
  const compReady = Boolean(compressedSrc)

  const words = useMemo(() => ["Kept", "exactly", "as", "you", "shot", "it."], [])

  return (
    <div className={styles.root}>
      <header className={styles.topbar}>
        <div className={styles.brand}>
          <svg viewBox="0 0 24 24" width="17" height="17" aria-hidden="true">
            <circle
              cx="12"
              cy="12"
              r="9"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
            />
            <path
              d="M12 3v18M3 12h18M5.6 5.6l12.8 12.8M18.4 5.6L5.6 18.4"
              stroke="currentColor"
              strokeWidth="1.1"
              opacity="0.55"
            />
            <circle cx="12" cy="12" r="2.6" fill="currentColor" />
          </svg>
          <span className={styles.brandMark}>Original</span>
          <span className={styles.brandSub}>CloudVault</span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <p className={styles.eyebrow}>Every photo, full resolution</p>
        <h1 className={styles.title}>
          {words.map((w, i) => (
            <motion.span
              key={i}
              initial={{ opacity: 0, y: 14 }}
              animate={mounted ? { opacity: 1, y: 0 } : {}}
              transition={{
                delay: reduce ? 0 : 0.1 + i * 0.06,
                duration: 0.5,
                ease: [0.22, 1, 0.36, 1],
              }}
            >
              {w}
            </motion.span>
          ))}
        </h1>
      </div>

      {/* THE MATTED PRINT — before/after with a draggable loupe */}
      <div className={styles.gallery}>
        <motion.div
          className={styles.mat}
          initial={{ opacity: 0, scale: 0.975, y: 10 }}
          animate={mounted ? { opacity: 1, scale: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            className={styles.stage}
            ref={stageRef}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            {/* base = the pristine ORIGINAL */}
            <img
              className={styles.orig}
              src={active.src}
              alt={active.title}
              draggable={false}
            />

            {/* overlay = COMPRESSED, clipped to the left of the divider */}
            <div
              className={styles.compWrap}
              style={{ clipPath: `inset(0 ${100 - pct}% 0 0)` }}
            >
              <img
                className={`${styles.comp} ${
                  compReady ? styles.compReady : ""
                }`}
                src={compReady ? compressedSrc : active.src}
                alt=""
                draggable={false}
              />
              <span className={styles.grain} aria-hidden="true" />
            </div>

            {/* corner tags */}
            <span className={`${styles.tag} ${styles.tagLeft}`}>
              Compressed elsewhere
            </span>
            <span className={`${styles.tag} ${styles.tagRight}`}>
              Your original
            </span>

            {/* the loupe / divider */}
            <div className={styles.divider} style={{ left: `${pct}%` }}>
              <span className={styles.dividerLine} />
              <span className={styles.handle}>
                <svg viewBox="0 0 24 24" width="18" height="18">
                  <path
                    d="M9.5 7.5L5 12l4.5 4.5M14.5 7.5L19 12l-4.5 4.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
            </div>
          </div>
        </motion.div>

        {/* EXIF readout — what each side actually keeps */}
        <div className={styles.exif}>
          <div className={`${styles.exifCol} ${styles.exifBad}`}>
            <span className={styles.exifWho}>Others downscale to</span>
            <span className={styles.exifData}>
              {active.lossyMp} MP · {active.lossySize}
            </span>
          </div>
          <span className={styles.exifRule} />
          <div className={`${styles.exifCol} ${styles.exifGood}`}>
            <span className={styles.exifWho}>CloudVault keeps</span>
            <span className={styles.exifData}>
              {active.mp} MP · {active.fmt} · {active.size}
            </span>
          </div>
        </div>

        {/* filmstrip — tap to compare another original */}
        <div className={styles.strip}>
          {PHOTOS.map((p, i) => (
            <button
              key={p.id}
              className={`${styles.thumb} ${
                i === selected ? styles.thumbOn : ""
              }`}
              onClick={() => pickPhoto(i)}
              aria-label={`Compare ${p.title}`}
            >
              <img src={p.src} alt="" draggable={false} />
            </button>
          ))}
          <span className={styles.stripCap}>{active.title}</span>
        </div>
      </div>

      {/* PLAN — yearly is the prominent figure */}
      <div className={styles.plan}>
        <p className={styles.planLede}>
          Full-resolution backup of <b>every</b> original — RAW, HEIC and all.
        </p>
        <div className={styles.priceRow}>
          <div className={styles.priceMain}>
            <span className={styles.priceYear}>{yearlyPrice}</span>
            <span className={styles.priceUnit}>/ year</span>
          </div>
          <div className={styles.priceAside}>
            <span className={styles.saveBadge}>Save {savePercent}%</span>
            <span className={styles.perMonth}>just {yearlyPerMonth}/mo</span>
          </div>
        </div>
        <button className={styles.cta} onClick={buyYearly}>
          Keep every original
        </button>
        <button className={styles.monthlyLink} onClick={buyMonthly}>
          or stay monthly at {monthlyPrice}/mo
        </button>
        <p className={styles.legal}>
          Cancel anytime — your originals always stay yours, at full quality.
        </p>
        <div className={styles.legalLinks}>
          <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
          <span>·</span>
          <button onClick={() => native.postNativeEvent("privacy")}>
            Privacy
          </button>
        </div>
      </div>
    </div>
  )
}

export default OriginalPaywall
